import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import BrandWordmark from "../components/BrandWordmark";
import { supabase } from "../supabase/supabase";

// The two links in the mailing list emails (migration 081):
//   /mailing-list/confirm?token=...       joins the list
//   /mailing-list/unsubscribe?token=...   leaves it
// Public and outside PrivateRoute: the reader has no account, and often opens
// the link on a different device from the one that signed up.
//
// The action runs on load, not on a button, because an unsubscribe has to work
// in one tap. Both are idempotent, so a link opened twice, or by an email
// scanner before the person, does no harm.

const ACTIONS = {
  confirm: {
    rpc: "confirm_mailing_list",
    working: "Confirming your address.",
    done: ["You are on the list.", "We will email once when next season opens."],
    gone: ["This link is no longer valid.", "Sign up again from the home page and we will send a new one."],
  },
  unsubscribe: {
    rpc: "unsubscribe_mailing_list",
    working: "Updating your preference.",
    done: ["You are unsubscribed.", "We will not email this address again."],
    gone: ["This link is not valid.", "If you keep getting email from us, write to the address on the Support page."],
  },
};

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export default function MailingListConfirm() {
  const { action } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const config = ACTIONS[action];
  const [state, setState] = useState("working"); // working | done | gone | failed

  useEffect(() => {
    if (!config) return undefined;
    if (!UUID.test(token)) {
      setState("gone");
      return undefined;
    }
    let cancelled = false;
    supabase
      .rpc(config.rpc, { p_token: token })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) setState("failed");
        else setState(data ? "done" : "gone");
      })
      .catch(() => { if (!cancelled) setState("failed"); });
    return () => { cancelled = true; };
  }, [config, token]);

  let heading;
  let body;
  if (!config) {
    [heading, body] = ["This link is not valid.", "Check that you opened the whole link from the email."];
  } else if (state === "working") {
    [heading, body] = [config.working, ""];
  } else if (state === "done") {
    [heading, body] = config.done;
  } else if (state === "failed") {
    [heading, body] = ["That did not go through.", "Open the link again in a minute."];
  } else {
    [heading, body] = config.gone;
  }

  return (
    <div
      className="theme-auto"
      style={{
        minHeight: "100vh",
        backgroundColor: "var(--color-bg-card)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "32px 24px",
        textAlign: "center",
      }}
    >
      <BrandWordmark width={200} style={{ marginBottom: 32 }} />
      <h1 style={{ fontSize: 20, fontWeight: 800, color: "var(--color-text-strong)", margin: "0 0 8px" }}>{heading}</h1>
      {body && <p style={{ fontSize: 15, lineHeight: 1.5, color: "var(--color-text-muted)", margin: "0 0 32px", maxWidth: 320 }}>{body}</p>}
      <Link to="/" style={{ color: "var(--color-cta)", fontSize: 15, fontWeight: 600, textDecoration: "none" }}>
        Back to Lineup →
      </Link>
    </div>
  );
}
